/**
 * 공유 날짜+시간 포맷 유틸
 *
 * B24: 중복 formatDateTime/formatDate 함수 통합
 * - formatDateTime: 날짜+시간 (year, month, day, hour, minute)
 * - formatDateTimeFull: 날짜+시간+초 (year, month, day, hour, minute, second)
 * - formatDateTimeShort: 짧은 날짜+시간 (month, day, hour, minute) — year 없음
 * - formatDateLocale: 날짜만 (year, month, day, weekday optional)
 * - formatMonthDayTime: "M/d HH:mm" (KST 고정, 월·일 0 채움 없음)
 */

import { format, getDay } from 'date-fns';
import { TZDate } from '@date-fns/tz';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export function formatKstDateTimeInput(iso: string): string {
  if (!iso) return '';
  return format(new TZDate(new Date(iso), 'Asia/Seoul'), "yyyy-MM-dd'T'HH:mm");
}

/**
 * ISO → "3/1 23:30" — 월·일은 안 채우고 시·분은 채운다. KST 고정.
 * 타임라인·승인 대기열의 짧은 시각 표기.
 * null/빈 문자열 → "-"
 */
export function formatMonthDayTime(iso: string | null): string {
  if (!iso) return '-';
  return format(new TZDate(new Date(iso), 'Asia/Seoul'), 'M/d HH:mm');
}

/**
 * ISO → "9/26" — 월·일을 안 채운다. KST 고정. 명단 확정일처럼 날짜만 짧게 보일 때.
 */
export function formatMonthDay(iso: string): string {
  return format(new TZDate(new Date(iso), 'Asia/Seoul'), 'M/d');
}

/**
 * ISO → "2025. 03. 01. 23:30"
 * null/빈 문자열 → "-"
 */
export function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return format(new Date(iso), 'yyyy. MM. dd. HH:mm');
}

/**
 * ISO → "2025. 03. 01. 23:30:45"
 * null/빈 문자열 → "-"
 */
export function formatDateTimeFull(iso: string | null): string {
  if (!iso) return '-';
  return format(new Date(iso), 'yyyy. MM. dd. HH:mm:ss');
}

/**
 * ISO → "03. 01. 23:30" — year 없음
 * null/빈 문자열 → "-"
 */
export function formatDateTimeShort(iso: string | null): string {
  if (!iso) return '-';
  return format(new Date(iso), 'MM. dd. HH:mm');
}

/**
 * ISO → "2025. 3. 1." 또는 "2025. 3. 1. (토)"
 * null/빈 문자열 → "-"
 */
export function formatDateLocale(iso: string | null, options?: { weekday?: boolean }): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const base = format(d, 'yyyy. M. d.');
  if (options?.weekday) {
    return `${base} (${WEEKDAYS[getDay(d)]})`;
  }
  return base;
}
